import React from "react";
import { SavedContent } from "../../types/api/SavedContent";
import { Card } from "../_ui/Card/Card";
import { HashtagList } from "../HashtagList";
import { PositionDropdown, PositionType } from "./PositionDropdown";
import { UseFormSetValue, UseFormWatch, FieldValues } from "react-hook-form";
import { AtSign, Hash, Layout, X, Menu } from "react-feather";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SavedContentCardProps {
  description: SavedContent;
  positions: {
    content: string;
    hashtag: string;
    tag: string;
  };
  onPositionChange: {
    content: (value: "USE_TEMPLATE" | "START" | "END" | "INLINE") => void;
    hashtag: (value: "START" | "END" | "INLINE") => void;
    tag: (value: "START" | "END" | "INLINE") => void;
  };
  hashtagsField: string;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  onRemove: () => void;
  isDraggable?: boolean;
}

const CONTENT_POSITION_OPTIONS = [
  { value: "USE_TEMPLATE" as PositionType, label: "Use Template" },
  { value: "START" as PositionType, label: "Start" },
  { value: "END" as PositionType, label: "End" },
  { value: "INLINE" as PositionType, label: "Inline" },
];

const POSITION_OPTIONS = [
  { value: "START" as PositionType, label: "Start" },
  { value: "END" as PositionType, label: "End" },
  { value: "INLINE" as PositionType, label: "Inline" },
];

export function SavedContentCard({
  description,
  positions,
  onPositionChange,
  hashtagsField,
  setValue,
  watch,
  onRemove,
  isDraggable = false,
}: SavedContentCardProps) {
  const hashtagCount = description.hashtags?.main?.length || 0;
  const variantCount = Object.keys(description.variants || {}).length;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({
    id: description.id,
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card className="bg-base-200 mb-0" noPadding noMargin>
      <div
        className="flex items-start gap-2 px-4 py-2"
        ref={setNodeRef}
        style={style}
        {...attributes}
      >
        {/* Drag handle */}
        {isDraggable && (
          <div
            className="flex items-center justify-center w-8 h-8 cursor-grab hover:bg-base-300 flex-shrink-0 transition-colors"
            ref={setActivatorNodeRef}
            {...listeners}
            title="Drag to reorder"
          >
            <Menu size={16} className="text-base-content/60" />
          </div>
        )}

        {/* Collapsible details */}
        <div className="collapse rounded-none">
          <input type="checkbox" className="peer" />
          <div className="collapse-title space-y-0.5 text-xs cursor-pointer font-medium text-base-content/70 py-0 px-0 min-h-0">
            {/* Compact main line */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <div className="prose flex-shrink-0">
                  <h4 className="mb-0">{description.name}</h4>
                </div>
                <span className="font-light text-sm flex-shrink-0">
                  ({description.slug})
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 flex-shrink-0">
                {hashtagCount > 0 && (
                  <div className="badge badge-xs badge-soft badge-accent">
                    {hashtagCount} hashtag{hashtagCount > 1 ? "s" : ""}
                  </div>
                )}
                {variantCount > 0 && (
                  <div className="badge badge-xs badge-soft badge-neutral">
                    {variantCount} variant{variantCount > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="collapse-content px-0 pb-0 pt-2">
            <div className="space-y-2">
              <hr className="w-full mt-2 pt-2" />

              <div className="flex items-center justify-start gap-1">
                <div className="text-xs font-medium text-base-content/70">
                  Position:
                </div>

                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                  <PositionDropdown
                    Icon={Layout}
                    value={positions.content}
                    options={CONTENT_POSITION_OPTIONS}
                    onChange={(value: string) =>
                      onPositionChange.content(
                        value as "USE_TEMPLATE" | "START" | "END" | "INLINE"
                      )
                    }
                    color="badge-primary"
                    hoverColor="hover:badge-accent"
                    width="w-36"
                  />
                  <PositionDropdown
                    Icon={Hash}
                    value={positions.hashtag}
                    options={POSITION_OPTIONS}
                    onChange={(value: string) =>
                      onPositionChange.hashtag(
                        value as "START" | "END" | "INLINE"
                      )
                    }
                    width="w-24"
                  />

                  <PositionDropdown
                    Icon={AtSign}
                    value={positions.tag}
                    options={POSITION_OPTIONS}
                    onChange={(value: string) =>
                      onPositionChange.tag(value as "START" | "END" | "INLINE")
                    }
                    width="w-24"
                  />
                </div>
              </div>

              {/* Variants */}
              {variantCount > 0 && (
                <div>
                  <div className="text-xs font-medium text-base-content/70 mb-2">
                    Variants:
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {Object.entries(description.variants).map(([slug]) => (
                      <div
                        key={slug}
                        className="badge badge-sm badge-neutral badge-soft"
                      >
                        {slug}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hashtags and position controls */}
              <div className="space-y-2">
                <div className="flex-1">
                  {hashtagCount > 0 && (
                    <div>
                      <div className="text-xs font-medium text-base-content/70 mb-2">
                        Hashtags:
                      </div>
                      <HashtagList
                        hashtags={description.hashtags?.main || []}
                        onHashtagsChange={(newHashtags) => {
                          setValue(
                            hashtagsField,
                            watch(hashtagsField).concat(newHashtags)
                          );
                        }}
                        displayOnly
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={onRemove}
            title="Remove from current feed"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </Card>
  );
}
